import React, { useState } from 'react';
import logo from '../assets/logo.png';
import { HiOutlineClock, HiPhone } from 'react-icons/hi';
import { Popover, SwipeableDrawer, List, Divider } from '@mui/material';
import { MoreVert, Menu, Home, Info, ContactPhone, Terminal } from '@mui/icons-material';
import { MENU, PAGES } from '../constants';
import { Box } from '@mui/system';
import { Twitter, Facebook, LinkedIn } from '@mui/icons-material';
import { NavLink, useNavigate } from 'react-router-dom';

export default function TopNav({ fromPrivacy }) {
    const navigation = useNavigate();

    // State for Popover anchor element to control popover placement properly
    const [anchorEl, setAnchorEl] = useState(null);

    // State for mobile menu drawer
    const [showmenupopup, setShowmenupopup] = useState(false);

    // Handlers for Popover open/close
    const handlePopOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopClose = () => {
        setAnchorEl(null);
    };

    const popOpen = Boolean(anchorEl);

    // Styles for NavLink active state
    const navLinkStyle = ({ isActive }) => ({
        backgroundColor: isActive ? '#E5E7EB' : 'transparent', // Tailwind gray-200
        color: isActive ? '#1A202C' : '#4A5568',               // dark gray active, medium gray default
        fontWeight: isActive ? '600' : '500',
    });

    // Mobile drawer content list
    const list = () => (
        <Box
            sx={{ width: 250 }}
            role="presentation"
            onClick={() => setShowmenupopup(false)}
            onKeyDown={() => setShowmenupopup(false)}
        >
            <div className='flex justify-center items-center h-24 gap-4'>
                <Twitter fontSize='large' sx={{ color: '#4A5568' }} />
                <Facebook fontSize='large' sx={{ color: '#4A5568' }} />
                <LinkedIn fontSize='large' sx={{ color: '#4A5568' }} />
            </div>
            <Divider />
            <List>
                {[{ title: MENU.HOME, path: PAGES.INDEX }, { title: MENU.ABOUT, path: PAGES.ABOUT },
                // { title: MENU.SERVICES, path: PAGES.SERVICES }, (commented out per your note)
                { title: MENU.CONTACTUS, path: PAGES.CONTACT }].map((item, index) => (
                    <NavLink
                        key={index}
                        to={item.path}
                        className='flex items-center px-6 gap-3 py-3 rounded-md mb-3 mx-6 transition-colors duration-200 hover:bg-gray-200'
                        style={navLinkStyle}
                    >
                        <div>
                            {index === 0 ? <Home sx={{ color: 'inherit' }} /> : index === 1 ? <Info sx={{ color: 'inherit' }} /> : index === 2 ? <Terminal sx={{ color: 'inherit' }} /> : <ContactPhone sx={{ color: 'inherit' }} />}
                        </div>
                        <p>{item.title}</p>
                    </NavLink>
                ))}
            </List>
        </Box>
    );

    return (
        <header className=" w-full h-16 lg:h-20 flex items-center justify-between px-6 md:px-20  bg-white shadow-sm fixed top-0 z-50">

            {/* Mobile / Tablet Header: Hamburger + Logo + More options */}
            <div className="lg:hidden flex items-center justify-between w-full gap-4">
                <div className="flex items-center gap-4">
                    {!fromPrivacy && (
                        <button
                            onClick={() => setShowmenupopup(true)}
                            aria-label="Open Menu"
                            className="focus:outline-none focus:ring-2 focus:ring-gray-300 rounded"
                        >
                            <Menu sx={{ fontSize: 20, color: '#4A5568' }} />
                        </button>
                    )}
                    <img
                        src={logo}
                        alt="GitSetCode Logo"
                        className="h-10 cursor-pointer"
                        onClick={() => navigation(PAGES.INDEX)}
                    />
                </div>
                <div onClick={handlePopOpen} className="cursor-pointer">
                    <MoreVert sx={{ fontSize: 20, color: '#4A5568' }} />
                </div>
            </div>

            {/* Desktop Header: Nav Links (shown only from lg and up) */}
            <nav className=" hidden lg:flex gap-8 items-center font-medium">

                <img
                    src={logo}
                    alt="GitSetCode Logo"
                    className="h-14 cursor-pointer"
                    onClick={() => navigation(PAGES.INDEX)}
                />

                {[{ title: MENU.HOME, path: PAGES.INDEX }, { title: MENU.ABOUT, path: PAGES.ABOUT },
                // { title: MENU.SERVICES, path: PAGES.SERVICES },
                { title: MENU.CONTACTUS, path: PAGES.CONTACT }].map((item, index) => (
                    <NavLink
                        key={index}
                        to={item.path}
                        className="px-4 py-3 rounded-md text-base transition-colors duration-200 hover:bg-gray-200 hover:text-gray-900"
                        style={navLinkStyle}
                    >
                        {item.title}
                    </NavLink>
                ))}
            </nav>

            {/* Desktop Header: Phone & Hours info (shown only from lg and up) */}
            <div className=" hidden lg:flex items-center gap-10 text-gray-500 select-none text-sm">
                <a href="tel:+917985957362" className="flex items-center gap-2 hover:text-gray-700 transition-colors duration-300 whitespace-nowrap">
                    <HiPhone className="text-secondary-color" style={{ color: '#6B7280' }} />
                    <span className="text-xs md:text-sm">+91 7985957362</span>
                </a>
            </div>

            {/* Popover for time and phone info on mobile */}
            <Popover
                id={popOpen ? 'mobile-info-popover' : undefined}
                open={popOpen}
                anchorEl={anchorEl}
                onClose={handlePopClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{ sx: { padding: 2, maxWidth: 260 } }}
            >
                <div>
                    <div className="flex items-center gap-3 mb-3">
                        <HiOutlineClock className="text-secondary-color" style={{ color: '#6B7280' }} />
                        <span className="text-gray-700">
                            09:00 <small className="text-[10px] md:text-xs">AM</small> — 05:00 <small className="text-[10px] md:text-xs">PM</small>
                        </span>
                    </div>
                    <a href="tel:+917985957362" className="flex items-center gap-3 hover:text-gray-900 transition-colors duration-200">
                        <HiPhone className="text-secondary-color" style={{ color: '#6B7280' }} />
                        <span className="text-gray-900 text-sm">+91 7985957362</span>
                    </a>
                </div>
            </Popover>

            {/* Swipeable Drawer for Mobile Menu */}
            <SwipeableDrawer
                anchor={'left'}
                open={showmenupopup}
                onClose={() => setShowmenupopup(false)}
                onOpen={() => { }}
                PaperProps={{ sx: { backgroundColor: '#FFFFFF' } }}
            >
                {list()}
            </SwipeableDrawer>

        </header>
    );
}
